import type { FetchDocumentTypesFn } from '@/features/documents'
import type { DocumentType } from '@/features/documents/DocumentType'
import type { AlertErrorHandlerFactory } from '@/features/shell'
import { AsyncPrompt } from '@/infra/AsyncPrompt'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { type Checklist } from '@/features/jobs/info-gathering/Checklist'
import { DocumentSupportingItemInput } from '@/features/jobs/info-gathering/documents/DocumentSupportingItemInput'
import { type DocumentRequestStore } from '@/features/jobs/details/document-requests/DocumentRequestStore'
import { action, computed, observable, runInAction } from 'mobx'
import { task } from 'mobx-task'
import type { Id } from '@/infra/id'

/**
 * View model for the dialog requesting documents from a job's client.
 */
export class DocumentRequestDialogViewModel {
  private readonly _prompt

  readonly modal = new ModalViewModel()

  @observable
  accessor selectedDocumentTypes: DocumentType[] = []

  /**
   * The document types of the workspace.
   */
  @observable
  accessor documentTypes: DocumentType[] = []

  constructor(
    private readonly _jobId: Id,
    private readonly _fetchDocumentTypes: FetchDocumentTypesFn,
    private readonly _documentRequestStore: DocumentRequestStore,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _getChecklist: () => Checklist | undefined,
  ) {
    this._prompt = new AsyncPrompt<DocumentType[]>({
      abort$: this.modal.close$,
    })
  }

  /**
   * Document types that clients upload, which are the ones that can be
   * requested.
   */
  @computed
  get requestableDocumentTypes() {
    return this.documentTypes.filter((t) => t.uploaderUserType === 'Client')
  }

  /**
   * The job's checklist, if it has one.
   */
  @computed
  get checklist() {
    return this._getChecklist()
  }

  /**
   * Prompts the user to create a document request.
   *
   * @returns The requested document types, or `null` when cancelled.
   */
  async promptForDocumentRequest(): Promise<DocumentType[] | null> {
    this.modal.close()
    // The dialog renders a failed load with the task's own state.
    void this.fetch().catch(() => undefined)

    const responsePromise = this._prompt.prompt()

    runInAction(() => {
      const checklist = this.checklist
      if (checklist) {
        this.selectedDocumentTypes = checklist.sections.items.flatMap((s) =>
          s.requestedSupportingItems.items
            .filter(
              (i) => i.adHoc && i.input instanceof DocumentSupportingItemInput,
            )
            .map((i) => i.input as DocumentSupportingItemInput)
            .filter((input) => input?.documentType !== undefined)
            .map((input) => input.documentType),
        )
      }
      this.modal.show()
    })

    const response = await responsePromise
    this.modal.close()
    return response
  }

  /**
   * Toggles a document type's selection.
   */
  @action.bound
  toggle(documentType: DocumentType) {
    if (this.selectedDocumentTypes.includes(documentType)) {
      this.selectedDocumentTypes = this.selectedDocumentTypes.filter(
        (dt) => dt !== documentType,
      )
    } else {
      this.selectedDocumentTypes = [...this.selectedDocumentTypes, documentType]
    }
  }

  /**
   * Fetches the document types the dialog offers.
   */
  fetch = task(async () => {
    const documentTypes = await this._fetchDocumentTypes()
    runInAction(() => {
      this.documentTypes = documentTypes
    })
  })

  /**
   * Requests the selected document types from the client.
   */
  submit = task.resolved(async () => {
    await this._documentRequestStore
      .submitDocumentRequest({
        jobId: this._jobId,
        documentTypes: this.selectedDocumentTypes,
      })
      .catch(this._createAlertErrorHandler('Requesting the documents failed'))

    this._prompt.respond(this.selectedDocumentTypes)
    this.close()
  })

  /**
   * Closes the dialog, resolving any pending prompt with `null`.
   */
  @action.bound
  close() {
    this.modal.close()
  }
}
